import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { CalendarDays, Check, Copy, Mail, Send, TriangleAlert } from 'lucide-react';
import { DefinitionList, Meter, SegmentedControl, Tag, useDelayedFlag } from '../ui';
import { focusRing, sbg, typeStyle } from '../../theme/sbgTokens';
import { MISSING, formatDate, formatDateRange, formatNumber, formatPercent } from '../../utils/format';
import { EASTERN_TIME_ZONE, addDaysToYmd, getTodayEt } from '../../utils/easternTime';

interface TimeOffConflict {
  employeeName: string;
  startDate: string;
  endDate: string;
  type: string;
}

interface EmailPreviewModalProps {
  onClose: () => void;
  to: string;
  subject: string;
  content: string;
  onSend?: () => void;
  isLoading?: boolean;
  error?: string;
  balanceWarning?: {
    type: 'PTO' | 'SICK' | 'WFH';
    remaining: number;
    total: number;
  };
  timeOffDetails?: {
    startDate: string;
    endDate: string;
    type: string;
    hours: number;
    notes?: string;
  };
  conflicts?: TimeOffConflict[];
  departmentCoverage?: number;
}

type PreviewView = 'email' | 'calendar';

/** Time-off type as it reads in a sentence ("Low sick balance"). */
const TYPE_IN_SENTENCE: Record<string, string> = { PTO: 'PTO', SICK: 'sick', WFH: 'WFH' };
/** Time-off type as a label ("Sick"). */
const TYPE_LABEL: Record<string, string> = { PTO: 'PTO', SICK: 'Sick', WFH: 'WFH' };

const formatHours = (value: number | null | undefined) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return MISSING;
  return `${formatNumber(value, Number.isInteger(value) ? 0 : 1)} ${value === 1 ? 'hour' : 'hours'}`;
};

/* ---- Calendar days ------------------------------------------------------ */
/*
 * Time-off dates are calendar dates (YYYY-MM-DD). The calendar compares them
 * as Eastern calendar days, never as instants: comparing "now" against a
 * midnight interval dropped the last day of every range, and a reversed range
 * made date-fns throw.
 */

const YMD = /^\d{4}-\d{2}-\d{2}$/;
const ET_YMD = new Intl.DateTimeFormat('en-CA', {
  timeZone: EASTERN_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** A date or timestamp as its Eastern calendar day (YYYY-MM-DD), or null. */
const toYmd = (value: string | null | undefined): string | null => {
  if (!value) return null;
  if (YMD.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : ET_YMD.format(date);
};

/** Whether a calendar day falls in a range, inclusive at both ends, in either order. */
const inRange = (day: string, start: string | null, end: string | null) => {
  if (!start || !end) return false;
  const [from, to] = start <= end ? [start, end] : [end, start];
  return day >= from && day <= to;
};

const dayPart = (ymd: string, options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...options }).format(new Date(`${ymd}T12:00:00Z`));

/* ---- Email text --------------------------------------------------------- */

const FIELD_LABEL = /(Start Date:|End Date:|Hours:|Type:)/;

/**
 * Renders the generated email as text: line breaks kept, field labels bold.
 * The body comes from a language model, so it is never injected as HTML.
 */
const renderEmailText = (text: string) =>
  text.split('\n').map((line, lineIndex) => (
    <React.Fragment key={lineIndex}>
      {lineIndex > 0 && <br />}
      {line.split(FIELD_LABEL).map((part, partIndex) =>
        partIndex % 2 === 1 ? <strong key={partIndex}>{part}</strong> : part
      )}
    </React.Fragment>
  ));

type CopyState = 'idle' | 'copied' | 'failed';
const COPY_CONFIRM_MS = 2000;

/** A small swatch for the calendar legend, drawn like the day cell it explains. */
const LegendSwatch: React.FC<{ bg: string; border: string; label: string; icon?: React.ReactNode }> = ({ bg, border, label, icon }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      aria-hidden="true"
      sx={{
        width: 14,
        height: 14,
        borderRadius: sbg.radius.xs,
        bgcolor: bg,
        border: `1px solid ${border}`,
        display: 'grid',
        placeItems: 'center',
        color: sbg.status.danger.icon,
        '& > svg': { width: 10, height: 10 },
      }}
    >
      {icon}
    </Box>
    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
      {label}
    </Typography>
  </Box>
);

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  onClose,
  to,
  subject,
  content,
  onSend,
  isLoading = false,
  error,
  balanceWarning,
  timeOffDetails,
  conflicts = [],
  departmentCoverage,
}) => {
  const [isSending, setIsSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const copyTimer = useRef<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<PreviewView>('email');
  const showBodySkeleton = useDelayedFlag(isLoading);
  const hasContent = Boolean(content && content.trim());
  const canCopy = !isLoading && hasContent;

  useEffect(() => () => window.clearTimeout(copyTimer.current), []);

  const handleSend = async () => {
    if (!onSend) return;

    setIsSending(true);
    setSendFailed(false);
    try {
      await onSend();
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      setSendFailed(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!canCopy) return;
    window.clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(content);
      setCopyState('copied');
      copyTimer.current = window.setTimeout(() => setCopyState('idle'), COPY_CONFIRM_MS);
    } catch {
      // No clipboard permission, or an insecure context without the API.
      setCopyState('failed');
    }
  };

  const copyTooltip = !canCopy
    ? 'Nothing to copy yet'
    : copyState === 'copied'
      ? 'Copied'
      : 'Copy email text';
  const copyButton = (
    <IconButton aria-label="Copy email text" onClick={handleCopyToClipboard} disabled={!canCopy}>
      {copyState === 'copied' ? <Check /> : <Copy />}
    </IconButton>
  );

  const remainingPercentage = balanceWarning
    ? (balanceWarning.remaining / balanceWarning.total) * 100
    : 0;

  // The calendar view only has something to show when the email is about a
  // time-off request; otherwise the preview is the email alone.
  const view: PreviewView = timeOffDetails ? activeTab : 'email';
  const typeLabel = timeOffDetails ? TYPE_LABEL[timeOffDetails.type] ?? timeOffDetails.type : '';

  // Simple calendar view for the time off period
  const renderCalendarView = () => {
    if (!timeOffDetails) return null;

    const startDay = toYmd(timeOffDetails.startDate);
    const endDay = toYmd(timeOffDetails.endDate);
    const conflictRanges = conflicts.map((conflict) => [toYmd(conflict.startDate), toYmd(conflict.endDate)] as const);

    // The next 14 Eastern calendar days, starting today.
    const today = getTodayEt();
    const dates = Array.from({ length: 14 }, (_, i) => {
      const day = addDaysToYmd(today, i);
      return {
        day,
        isTimeOff: inRange(day, startDay, endDay),
        hasConflict: conflictRanges.some(([from, to]) => inRange(day, from, to)),
      };
    });

    return (
      <Box component="section" aria-labelledby="email-preview-calendar-title" sx={{ display: 'grid', gap: 1.5 }}>
        <Box>
          <Typography id="email-preview-calendar-title" variant="labelMd" component="h3" sx={{ color: sbg.text.primary, display: 'block' }}>
            Next 14 days
          </Typography>
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
            Starting today, {formatDate(today, { short: true, weekday: true })}
          </Typography>
        </Box>

        <Box
          component="ol"
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            gap: 1,
          }}
        >
          {dates.map((dateInfo) => {
            const conflicted = Boolean(dateInfo.isTimeOff && dateInfo.hasConflict);
            const tone = conflicted
              ? { bg: sbg.status.danger.bg, border: sbg.status.danger.border, text: sbg.status.danger.text }
              : dateInfo.isTimeOff
                ? { bg: sbg.bg.selected, border: sbg.border.selected, text: sbg.text.accent }
                : { bg: sbg.bg.surface, border: sbg.border.default, text: sbg.text.primary };
            const spoken =
              `${dayPart(dateInfo.day, { weekday: 'long', month: 'long', day: 'numeric' })}` +
              `${dateInfo.isTimeOff ? ', your time off' : ''}` +
              `${conflicted ? ', conflicts with a teammate' : ''}`;
            return (
              <Box
                component="li"
                key={dateInfo.day}
                sx={{
                  position: 'relative',
                  minWidth: 0,
                  height: 56,
                  display: 'grid',
                  alignContent: 'center',
                  justifyItems: 'center',
                  gap: 0.25,
                  borderRadius: sbg.radius.md,
                  border: `1px solid ${tone.border}`,
                  bgcolor: tone.bg,
                }}
              >
                <span className="sbg-sr-only">{spoken}</span>
                <Typography variant="caption" component="span" aria-hidden="true" sx={{ color: sbg.text.secondary }}>
                  {dayPart(dateInfo.day, { weekday: 'short' })}
                </Typography>
                <Typography
                  variant="labelMd"
                  component="span"
                  aria-hidden="true"
                  sx={{ color: tone.text, fontVariantNumeric: 'tabular-nums' }}
                >
                  {dayPart(dateInfo.day, { day: 'numeric' })}
                </Typography>
                {conflicted && (
                  <Box
                    aria-hidden="true"
                    sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', color: sbg.status.danger.icon }}
                  >
                    <TriangleAlert size={12} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
          <LegendSwatch bg={sbg.bg.selected} border={sbg.border.selected} label="Your time off" />
          <LegendSwatch
            bg={sbg.status.danger.bg}
            border={sbg.status.danger.border}
            label="Conflicts with a teammate"
            icon={<TriangleAlert />}
          />
        </Box>
      </Box>
    );
  };

  const metaItems: { label: React.ReactNode; value: React.ReactNode }[] = [{ label: 'To', value: to || MISSING }];
  if (timeOffDetails) {
    metaItems.push({
      label: 'Time off',
      value: (
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {typeLabel} · {formatDateRange(timeOffDetails.startDate, timeOffDetails.endDate)} · {formatHours(timeOffDetails.hours)}
        </Box>
      ),
    });
  }

  return (
    <>
      {/*
        The copy button and its live region sit beside DialogTitle, not inside
        it: the Dialog takes its accessible name from DialogTitle, which should
        read only "Email preview" and the subject.
      */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <DialogTitle component="div" sx={{ flex: 1, minWidth: 0, pr: view === 'email' ? 1 : undefined }}>
          <Typography variant="overline" component="p" sx={{ color: sbg.text.secondary, display: 'block', mb: 0.5 }}>
            Email preview
          </Typography>
          <Typography
            variant="h3"
            component="h2"
            sx={{ color: subject ? sbg.text.primary : sbg.text.secondary, overflowWrap: 'anywhere' }}
          >
            {subject || 'No subject'}
          </Typography>
        </DialogTitle>
        {view === 'email' && (
          <Box sx={{ flex: 'none', pt: 2.5, pr: 2 }}>
            <Tooltip title={copyTooltip}>
              {canCopy ? copyButton : <Box component="span" sx={{ display: 'inline-flex' }}>{copyButton}</Box>}
            </Tooltip>
          </Box>
        )}
        <span className="sbg-sr-only" aria-live="polite">
          {copyState === 'copied' ? 'Email text copied' : copyState === 'failed' ? 'Couldn’t copy the email text' : ''}
        </span>
      </Box>

      <DialogContent sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
        {error && (
          <Alert severity="error">
            <AlertTitle>This email can’t be sent</AlertTitle>
            {error}
          </Alert>
        )}

        {sendFailed && (
          <Alert severity="error">
            <AlertTitle>Couldn’t send the email</AlertTitle>
            Try again in a moment. If it keeps failing, copy the text and send it from your own inbox.
          </Alert>
        )}

        {copyState === 'failed' && (
          <Alert severity="error">
            Couldn’t copy the email text. Select the text in the preview and copy it manually.
          </Alert>
        )}

        {balanceWarning && remainingPercentage < 25 && (
          <Alert severity="warning">
            <AlertTitle>Low {TYPE_IN_SENTENCE[balanceWarning.type] ?? balanceWarning.type} balance</AlertTitle>
            <Box component="span" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
              {formatHours(balanceWarning.remaining)} left, {formatPercent(remainingPercentage)} of the{' '}
              {formatHours(balanceWarning.total)} allotment.
            </Box>
            <Meter
              value={balanceWarning.remaining}
              max={balanceWarning.total}
              tone="warning"
              aria-label={`${TYPE_LABEL[balanceWarning.type] ?? balanceWarning.type} balance remaining`}
              sx={{ mt: 1 }}
            />
          </Alert>
        )}

        {conflicts && conflicts.length > 0 && (
          <Alert severity="warning">
            <AlertTitle>
              {conflicts.length === 1 ? 'One scheduling conflict' : `${formatNumber(conflicts.length)} scheduling conflicts`}
            </AlertTitle>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gap: 0.25, fontVariantNumeric: 'tabular-nums' }}>
              {conflicts.map((conflict, index) => (
                <li key={index}>
                  {conflict.employeeName} · {TYPE_LABEL[conflict.type] ?? conflict.type} ·{' '}
                  {formatDateRange(conflict.startDate, conflict.endDate)}
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {departmentCoverage !== undefined && departmentCoverage < 50 && (
          <Alert severity="info">
            <AlertTitle>Low department coverage</AlertTitle>
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              Only {formatPercent(departmentCoverage)} of your department will be available during this time.
            </Box>
          </Alert>
        )}

        {timeOffDetails && (
          <SegmentedControl<PreviewView>
            value={view}
            onChange={setActiveTab}
            size="small"
            aria-label="Preview view"
            sx={{ justifySelf: 'start' }}
            options={[
              { value: 'email', label: 'Email', icon: <Mail size={16} aria-hidden="true" /> },
              { value: 'calendar', label: 'Calendar', icon: <CalendarDays size={16} aria-hidden="true" /> },
            ]}
          />
        )}

        {view === 'email' ? (
          <>
            <DefinitionList items={metaItems} sx={{ '& > div': { gridTemplateColumns: '88px minmax(0, 1fr)' } }} />

            <Box sx={{ bgcolor: sbg.bg.canvas, borderRadius: sbg.radius.lg, p: { xs: 1, sm: 2 } }}>
              <Box
                role="region"
                aria-label="Email body"
                aria-busy={isLoading || undefined}
                tabIndex={0}
                sx={{
                  maxWidth: 600,
                  mx: 'auto',
                  maxHeight: 400,
                  overflow: 'auto',
                  p: { xs: 2, sm: 3 },
                  bgcolor: sbg.bg.surface,
                  border: `1px solid ${sbg.border.default}`,
                  borderRadius: sbg.radius.md,
                  color: sbg.text.primary,
                  ...typeStyle('body-md'),
                  '&:focus-visible': focusRing,
                  '& strong': { color: sbg.text.primary, fontWeight: 600 },
                }}
              >
                {isLoading ? (
                  <Box role="status" aria-label="Loading email" sx={{ display: 'grid', gap: 1.25, visibility: showBodySkeleton ? 'visible' : 'hidden' }}>
                    {['42%', '96%', '88%', '93%', '64%', '0', '90%', '72%'].map((width, index) =>
                      width === '0' ? (
                        <Box key={index} sx={{ height: 8 }} />
                      ) : (
                        <Skeleton key={index} variant="rounded" height={14} width={width} />
                      )
                    )}
                  </Box>
                ) : hasContent ? (
                  <div className="email-content">{renderEmailText(content)}</div>
                ) : (
                  <Typography variant="body1" component="p" sx={{ m: 0, color: sbg.text.secondary }}>
                    This email has no text yet. Close the preview and generate it again.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* A draft-only preview (no onSend) is never sent from Attendance; say how it gets out. */}
            {!onSend && hasContent && !isLoading && (
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
                Attendance doesn’t send this draft. Copy the text into a new email to {to || 'the employee'}.
              </Typography>
            )}
          </>
        ) : (
          <>
            {timeOffDetails ? (
              <Box component="section" aria-labelledby="email-preview-details-title" sx={{ display: 'grid', gap: 0.5 }}>
                <Typography id="email-preview-details-title" variant="labelMd" component="h3" sx={{ color: sbg.text.primary, display: 'block' }}>
                  Time off details
                </Typography>
                <DefinitionList
                  columns={2}
                  items={[
                    { label: 'Type', value: <Tag>{typeLabel}</Tag> },
                    { label: 'Hours', value: <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatHours(timeOffDetails.hours)}</Box> },
                    { label: 'Starts', value: formatDate(timeOffDetails.startDate) },
                    { label: 'Ends', value: formatDate(timeOffDetails.endDate) },
                    ...(timeOffDetails.notes ? [{ label: 'Notes', value: timeOffDetails.notes }] : []),
                  ]}
                />
              </Box>
            ) : null}

            {renderCalendarView()}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSending}>
          Close
        </Button>
        {onSend && (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={isLoading || isSending || !!error}
            aria-busy={isSending || undefined}
            startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <Send />}
          >
            {isSending ? 'Sending…' : 'Send email'}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default EmailPreviewModal;
